import React, { useState, useEffect, useRef, useCallback } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import './index.scss';

// ─── Mock Data ────────────────────────────────────────────────
var MOCK_COMMENTS = [
    { id: 'c1', code: 'c1', description: 'Đã kiểm tra số liệu đơn hàng, mọi thứ khớp.', user_name: 'Nguyễn Văn A', user_code: 'NVA', created_at: Date.now() - 7200000 },
    { id: 'c2', code: 'c2', description: 'Cần xác nhận lại giá chiết khấu với khách hàng.', user_name: 'Trần Thị B', user_code: 'TTB', created_at: Date.now() - 3600000 },
    { id: 'c3', code: 'c3', description: 'Đã liên hệ KH, xác nhận giá ok. Có thể release phiếu.', user_name: 'Nguyễn Văn A', user_code: 'NVA', created_at: Date.now() - 1800000 },
    { id: 'c4', code: 'c4', description: '@Trần Thị B: đã duyệt phiếu, bạn kiểm tra lại giúp.', user_name: 'Lê Văn C', user_code: 'LVC', created_at: Date.now() - 600000 }
];

// ─── Relative time ──────────────────────────────────────────────
function getRelativeTime(timestamp) {
    if (!timestamp) return '';
    var ts = typeof timestamp === 'number'
        ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
        : new Date(timestamp).getTime();
    var diff = Date.now() - ts;
    if (diff < 0) diff = 0;
    var mins = Math.floor(diff / 60000);
    var hours = Math.floor(diff / 3600000);
    var days = Math.floor(diff / 86400000);

    if (mins < 1) return AntLocalization.translateText({ vi: 'Vừa xong', en: 'Just now', fr: 'À l\'instant', es: 'Ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी', ar: 'الآن' });
    if (mins < 60) return mins + AntLocalization.translateText({ vi: ' phút trước', en: 'm ago', fr: ' min', es: ' min', id: ' mnt lalu', ja: '分前', zh: '分钟前', ko: '분 전', hi: ' मि. पहले', ar: ' د' });
    if (hours < 24) return hours + AntLocalization.translateText({ vi: ' giờ trước', en: 'h ago', fr: ' h', es: ' h', id: ' jam lalu', ja: '時間前', zh: '小时前', ko: '시간 전', hi: ' घं. पहले', ar: ' س' });
    if (days < 7) return days + AntLocalization.translateText({ vi: ' ngày trước', en: 'd ago', fr: ' j', es: ' d', id: ' hari lalu', ja: '日前', zh: '天前', ko: '일 전', hi: ' दिन पहले', ar: ' ي' });

    var d = new Date(ts);
    return d.toLocaleDateString('vi-VN');
}

// ─── Avatar helpers ─────────────────────────────────────────────
function getInitials(name) {
    if (!name) return '?';
    var parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
}

function getAvatarColor(name) {
    if (!name) return '#9CA3AF';
    var hash = 0;
    for (var i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    var colors = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F97316', '#6366F1'];
    return colors[Math.abs(hash) % colors.length];
}

// ─── Build threads from flat list ───────────────────────────────
function buildThreads(flatComments, maxDepth) {
    if (!flatComments || flatComments.length === 0) return [];
    var byCode = {};
    var roots = [];

    for (var i = 0; i < flatComments.length; i++) {
        var c = flatComments[i];
        byCode[c.code] = { ...c, children: [] };
    }

    for (var j = 0; j < flatComments.length; j++) {
        var item = byCode[flatComments[j].code];
        if (item.reply_code && byCode[item.reply_code]) {
            byCode[item.reply_code].children.push(item);
        } else {
            roots.push(item);
        }
    }

    // Flatten threads beyond maxDepth
    function flattenDeep(node, depth) {
        if (!node.children) return;
        var overflow = [];
        for (var k = 0; k < node.children.length; k++) {
            if (depth + 1 >= maxDepth) {
                overflow.push(node.children[k]);
                node.children[k]._flatDepth = maxDepth;
            }
            flattenDeep(node.children[k], depth + 1);
        }
    }

    for (var r = 0; r < roots.length; r++) {
        flattenDeep(roots[r], 0);
    }

    return roots;
}

// ─── CommentInput ───────────────────────────────────────────────
function CommentInput({ onSubmit, replyTo, onCancelReply, placeholder, sending }) {
    var [text, setText] = useState('');
    var textareaRef = useRef(null);

    useEffect(() => {
        if (replyTo && textareaRef.current) textareaRef.current.focus();
    }, [replyTo]);

    var handleKeyDown = useCallback(function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (text.trim() && !sending) {
                onSubmit(text.trim());
                setText('');
            }
        }
    }, [text, sending, onSubmit]);

    var handleSend = useCallback(function () {
        if (text.trim() && !sending) {
            onSubmit(text.trim());
            setText('');
        }
    }, [text, sending, onSubmit]);

    var placeholderText = placeholder
        ? AntLocalization.translateText(placeholder)
        : AntLocalization.translateText({ vi: 'Viết bình luận...', en: 'Write a comment...', fr: 'Commenter...', es: 'Comentar...', id: 'Tulis komentar...', ja: 'コメントを入力...', zh: '写评论...', ko: '댓글 작성...', hi: 'टिप्पणी लिखें...', ar: 'اكتب تعليقًا...' });

    return (
        <div className="comment-input">
            {replyTo && (
                <div className="reply-indicator">
                    <span>{AntLocalization.translateText({ vi: 'Trả lời', en: 'Replying to', fr: 'Réponse à', es: 'Respondiendo a', id: 'Membalas', ja: '返信先', zh: '回复', ko: '답글 대상', hi: 'जवाब', ar: 'الرد على' })} <b>{replyTo.user_name}</b></span>
                    <button className="reply-cancel" onClick={onCancelReply}><i className="dx-icon-close" /></button>
                </div>
            )}
            <div className="comment-input-row">
                <textarea
                    ref={textareaRef}
                    className="comment-textarea"
                    value={text}
                    onChange={function (e) { setText(e.target.value); }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholderText}
                    rows={1}
                />
                <button
                    className={'comment-send-btn' + (text.trim() ? ' active' : '')}
                    onClick={handleSend}
                    disabled={!text.trim() || sending}
                >
                    <i className="dx-icon-email" />
                </button>
            </div>
        </div>
    );
}

// ─── CommentItem (recursive) ────────────────────────────────────
function CommentItem({ item, depth, session, allowReply, allowDelete, threadDepth, onReply, onDelete }) {
    var userName = item.user_name || '';
    var isOwn = session && session.user_code && item.user_code === session.user_code;
    var canReply = allowReply && depth < threadDepth;
    var canDelete = allowDelete && isOwn;

    return (
        <div className={'comment-item depth-' + Math.min(depth, 2)}>
            <div className="comment-avatar" style={{ background: getAvatarColor(userName) }}>
                {getInitials(userName)}
            </div>
            <div className="comment-body">
                <div className="comment-header">
                    <span className="comment-user">{userName}</span>
                    <span className="comment-time">{getRelativeTime(item.created_at)}</span>
                </div>
                <div className="comment-content">{item.description}</div>
                <div className="comment-actions">
                    {canReply && (
                        <button className="comment-action-btn" onClick={function () { onReply(item); }}>
                            {AntLocalization.translateText({ vi: 'Trả lời', en: 'Reply', fr: 'Répondre', es: 'Responder', id: 'Balas', ja: '返信', zh: '回复', ko: '답글', hi: 'जवाब दें', ar: 'رد' })}
                        </button>
                    )}
                    {canDelete && (
                        <button className="comment-action-btn delete" onClick={function () { onDelete(item); }}>
                            {AntLocalization.translateText({ vi: 'Xóa', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' })}
                        </button>
                    )}
                </div>
                {item.children && item.children.length > 0 && (
                    <div className="comment-children">
                        {item.children.map(function (child) {
                            return (
                                <CommentItem
                                    key={child.code || child.id}
                                    item={child}
                                    depth={depth + 1}
                                    session={session}
                                    allowReply={allowReply}
                                    allowDelete={allowDelete}
                                    threadDepth={threadDepth}
                                    onReply={onReply}
                                    onDelete={onDelete}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main Component ─────────────────────────────────────────────
export default function AntCommentBox({
    API,
    session,
    voucherType,
    voucherCode,
    voucherId,
    items,
    maxItems = 50,
    allowReply = true,
    allowDelete = true,
    threadDepth = 2,
    sortOrder = 'newest',
    compact,
    height,
    placeholder,
    onCommentCreated,
    mockMode = false,
    visible
}) {
    // ─── Visibility check via AntCondition ────────────────────
    if (visible && session) {
        var isVis = AntCondition.checkAll({}, visible, session);
        if (!isVis) return null;
    }

    var [comments, setComments] = useState(mockMode ? MOCK_COMMENTS : []);
    var [loading, setLoading] = useState(false);
    var [sending, setSending] = useState(false);
    var [replyTo, setReplyTo] = useState(null);
    var listRef = useRef(null);

    // ─── Load comments ──────────────────────────────────────────
    var loadComments = useCallback(function () {
        if (mockMode) {
            setComments(MOCK_COMMENTS);
            return;
        }
        if (items) {
            setComments(items);
            return;
        }
        if (!API || !voucherType) return;

        setLoading(true);
        var params = {
            voucher_type: voucherType,
            pageSize: maxItems,
            orderBy: 'created_at',
            orderType: sortOrder === 'newest' ? 'DESC' : 'ASC'
        };
        if (voucherCode) params.voucher_code = voucherCode;

        API.getList(params).then(function (result) {
            setComments(result && result.rows ? result.rows : []);
            setLoading(false);
        }).catch(function () {
            setLoading(false);
        });
    }, [API, voucherType, voucherCode, items, maxItems, sortOrder, mockMode]);

    useEffect(function () {
        loadComments();
    }, [loadComments]);

    // ─── Create comment ─────────────────────────────────────────
    var handleSubmit = useCallback(function (text) {
        if (!API || !text) return;

        setSending(true);
        var body = {
            voucher_type: voucherType,
            description: text
        };
        if (voucherCode) body.voucher_code = voucherCode;
        if (voucherId) body.voucher_id = voucherId;
        if (replyTo) body.reply_code = replyTo.code;

        var url = '/' + API.routeURL + '/create';
        API.post(url, body, function (result) {
            setSending(false);
            if (result && result.data) {
                setReplyTo(null);
                loadComments();
                if (onCommentCreated) onCommentCreated(result.data);
            }
        });
    }, [API, voucherType, voucherCode, voucherId, replyTo, loadComments, onCommentCreated]);

    // ─── Delete comment ─────────────────────────────────────────
    var handleDelete = useCallback(function (item) {
        if (!API || !item) return;

        var url = '/' + API.routeURL + '/delete';
        API.post(url, { id: item.id }, function (result) {
            if (result && !result.error) {
                loadComments();
            }
        });
    }, [API, loadComments]);

    // ─── Build thread tree ──────────────────────────────────────
    var sortedComments = comments.slice();
    if (sortOrder === 'newest') {
        sortedComments.sort(function (a, b) {
            var ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            var tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            return tb - ta;
        });
    } else {
        sortedComments.sort(function (a, b) {
            var ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            var tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            return ta - tb;
        });
    }

    var threads = buildThreads(sortedComments, threadDepth);

    // ─── Render ─────────────────────────────────────────────────
    var containerStyle = {};
    if (height) containerStyle.maxHeight = height;

    return (
        <div className={'ant-comment-box' + (compact ? ' compact' : '')}>
            <CommentInput
                onSubmit={handleSubmit}
                replyTo={replyTo}
                onCancelReply={function () { setReplyTo(null); }}
                placeholder={placeholder}
                sending={sending}
            />

            <div className="comment-list" ref={listRef} style={containerStyle}>
                {loading && (
                    <div className="comment-loading">
                        {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}
                    </div>
                )}

                {!loading && threads.length === 0 && (
                    <div className="comment-empty">
                        {AntLocalization.translateText({ vi: 'Chưa có bình luận', en: 'No comments yet', fr: 'Aucun commentaire', es: 'Sin comentarios', id: 'Belum ada komentar', ja: 'コメントなし', zh: '暂无评论', ko: '아직 댓글이 없습니다', hi: 'अभी कोई टिप्पणी नहीं', ar: 'لا توجد تعليقات بعد' })}
                    </div>
                )}

                {!loading && threads.map(function (item) {
                    return (
                        <CommentItem
                            key={item.code || item.id}
                            item={item}
                            depth={0}
                            session={session}
                            allowReply={allowReply}
                            allowDelete={allowDelete}
                            threadDepth={threadDepth}
                            onReply={function (c) { setReplyTo(c); }}
                            onDelete={handleDelete}
                        />
                    );
                })}
            </div>
        </div>
    );
}
